import { useLayoutEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from '../ui';

export interface DataColumn {
  key: string;
  header: ReactNode;
  /** `num` right-aligns the header (put `className="num"` on the cells) and uses tabular figures. */
  align?: 'start' | 'num';
  /** Header text only for screen readers (an actions column). */
  srOnly?: boolean;
}

/** Rows that carry an inline error, a confirm step or a detail panel; they are skipped when moving between rows. */
export const AUX_ROW = 'table__detail';

const ROW_SELECTOR = `:scope > tr:not(.${AUX_ROW})`;

/**
 * A table that can be driven from the keyboard: one row is in the tab order
 * (roving tabindex), ↑/↓ move between rows, Home/End jump to the first and
 * last, Enter or Space runs the row's primary action (the first control
 * marked `data-primary`, else the first link or button in the row), and Tab
 * leaves the row for its controls and then the table. Pages render their own
 * `<tr>` children so row components keep their hooks; auxiliary rows carry
 * the `table__detail` class and are not navigable.
 *
 * Columns declare `align: 'num'` for numeric data; the header cells keep the
 * mono uppercase style at the text-safe --ink-3.
 */
export function DataTable({
  columns,
  children,
  label,
  sticky = false,
  nested = false,
  className,
}: {
  columns: DataColumn[];
  children: ReactNode;
  /** Accessible name for the table. */
  label: string;
  /** Keep the header row visible while `.table-wrap` scrolls. */
  sticky?: boolean;
  /** The smaller variant for a table inside a detail row. */
  nested?: boolean;
  className?: string;
}) {
  const body = useRef<HTMLTableSectionElement>(null);

  // Roving tabindex: after every render exactly one data row is tabbable — the
  // one that had focus, else the first. Rows come from the page, so this is
  // done on the DOM rather than through props.
  useLayoutEffect(() => {
    const rows = navigableRows(body.current);
    if (rows.length === 0) return;
    const current = rows.find((r) => r.tabIndex === 0) ?? rows[0]!;
    for (const row of rows) row.tabIndex = row === current ? 0 : -1;
  });

  const onKeyDown = (e: KeyboardEvent<HTMLTableSectionElement>) => {
    const row = (e.target as HTMLElement).closest<HTMLTableRowElement>('tr');
    // Keys typed into a control inside the row belong to that control.
    if (!row || e.target !== row) return;
    const rows = navigableRows(body.current);
    const index = rows.indexOf(row);
    if (index === -1) return;
    let next: HTMLTableRowElement | undefined;
    switch (e.key) {
      case 'ArrowDown':
        next = rows[index + 1];
        break;
      case 'ArrowUp':
        next = rows[index - 1];
        break;
      case 'Home':
        next = rows[0];
        break;
      case 'End':
        next = rows[rows.length - 1];
        break;
      case 'Enter':
      case ' ': {
        e.preventDefault();
        primaryControl(row)?.click();
        return;
      }
      default:
        return;
    }
    e.preventDefault();
    if (!next || next === row) return;
    row.tabIndex = -1;
    next.tabIndex = 0;
    next.focus();
  };

  return (
    <div className={cx('table-wrap', sticky && 'table-wrap--sticky')}>
      <table className={cx('table', nested && 'table--nested', className)} aria-label={label}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={cx(c.align === 'num' && 'num')}
                aria-label={c.srOnly && typeof c.header === 'string' ? c.header : undefined}
              >
                {c.srOnly ? null : c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody ref={body} onKeyDown={onKeyDown}>
          {children}
        </tbody>
      </table>
    </div>
  );
}

function navigableRows(tbody: HTMLTableSectionElement | null): HTMLTableRowElement[] {
  return tbody ? Array.from(tbody.querySelectorAll<HTMLTableRowElement>(ROW_SELECTOR)) : [];
}

function primaryControl(row: HTMLTableRowElement): HTMLElement | null {
  return (
    row.querySelector<HTMLElement>('[data-primary]') ??
    row.querySelector<HTMLElement>('a[href], button:not(:disabled)')
  );
}
